import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FadeUp, MaskReveal, Stagger, StaggerItem } from "@/components/Motion";
import useSEO, { canonicalFor } from "@/lib/useSEO";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3e477c2a-f98b-44c6-92b8-bae42dd1c756/images/d6795f3d2d8957a22a2d44bb7cab186f58e3af4a35b7d231a5a6732c6ca5ce52.png";

export default function Services() {
  useSEO({
    title: "Practice areas — Corpora Consultants",
    description: "Corporate, financial and regulatory law services in Cyprus: CIF & CASP licensing, MiCA & MiFID II, tax and NID/IP Box structuring, banking, immigration and HQ relocation.",
    canonical: canonicalFor("/services"),
  });

  const { t } = useTranslation();
  const items = t("services.items", { returnObjects: true });

  return (
    <div data-testid="services-page">
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden grain">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "brightness(0.5)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,11,13,0.6) 0%, rgba(10,11,13,0.4) 35%, rgba(10,11,13,0.95) 100%)" }} />
        <div className="relative z-10 h-full max-w-[1480px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-20">
          <div className="overline mb-6">{t("services.eyebrow")}</div>
          <h1 className="display text-[var(--bone)]" style={{ fontSize: "clamp(64px, 10vw, 160px)" }}>
            <div><MaskReveal>{t("services.heroLine1")}</MaskReveal></div>
            <div className="display-italic" style={{ color: "var(--pewter)" }}>
              <MaskReveal delay={0.2}>{t("services.heroLine2")}</MaskReveal>
            </div>
          </h1>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
            <div className="md:col-span-5">
              <FadeUp>
                <p className="display text-2xl md:text-3xl leading-snug italic text-[var(--bone)]" style={{ fontWeight: 300 }}>
                  “{t("services.introQuote")}”
                </p>
              </FadeUp>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <FadeUp delay={0.1}>
                <p className="text-[var(--mute)] leading-[1.8]">{t("services.introBody")}</p>
              </FadeUp>
            </div>
          </div>

          <Stagger className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[var(--line)]">
            {items.map((s, i) => (
              <StaggerItem key={i} className="bg-[var(--ink)] p-10 md:p-14 min-h-[440px] flex flex-col justify-between border border-transparent svc-card">
                <div className="flex items-start justify-between mb-10">
                  <span className="font-mono text-[11px] tracking-[0.22em] text-[var(--mute-2)]">
                    {String(i + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                  </span>
                  <ArrowUpRight size={18} className="text-[var(--mute-2)]" />
                </div>
                <div>
                  <h3 className="display text-4xl md:text-5xl text-[var(--bone)] leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-5 text-[var(--bone-2)] leading-relaxed text-lg max-w-md">
                    {s.summary}
                  </p>
                  <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
                    {s.items.map((it) => (
                      <li key={it} className="flex items-start gap-3 text-[var(--mute)] text-[15px] leading-relaxed">
                        <span className="mt-2 inline-block w-3 h-px shrink-0" style={{ background: "var(--pewter)" }} />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="border-t border-[var(--line)] pt-20 grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
            <div className="md:col-span-7">
              <h2 className="display text-[var(--bone)]" style={{ fontSize: "clamp(48px, 7vw, 110px)" }}>
                {t("services.ctaTitleA")}{" "}
                <span className="display-italic" style={{ color: "var(--pewter)" }}>
                  {t("services.ctaTitleB")}
                </span>
              </h2>
              <p className="mt-6 text-[var(--bone-2)] text-lg max-w-xl leading-relaxed">
                {t("services.ctaBody")}
              </p>
            </div>
            <div className="md:col-span-5 md:flex md:justify-end">
              <Link to="/contact" className="btn-primary" data-testid="services-cta">
                {t("services.ctaBtn")} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
